import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { X, FilePlus2 } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { backendClient } from "../../../api/backend";
import { resetContract } from "../../../redux/actions/contractAction";
import { useSearchTokens } from "../../SearchScreen/searchDesignTokens";
import { isMobile } from "../../../utils/utils";

import TemplateOption from './components/TemplateOption';
import ImportOption from './components/ImportOption';
import EmptyOption from './components/EmptyOption';
import MyTemplatesOption from './components/MyTemplatesOption';

const CreateContractDialog = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user?.user);
  const dk = useSearchTokens();
  const inputRef = useRef(null);
  const mobile = isMobile();

  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contractName, setContractName] = useState("");
  const [error, setError] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("AE");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [contractTypeName, setContractTypeName] = useState("");
  // Sub-jurisdiction scoping for the chosen country. null = all jurisdictions
  // in the country (default). Array of codes = explicit selection.
  const [selectedJurisdictionCodes, setSelectedJurisdictionCodes] = useState(null);
  const [type, setType] = useState("templates");
  const [importedFile, setImportedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const supportedFormats = [
    { value: 'docx', label: 'Word (.docx)' },
    { value: 'pdf', label: 'PDF (.pdf)' },
    { value: 'txt', label: 'Text (.txt)' },
  ];

  useEffect(() => {
    if (open) resetForm();
  }, [open]);

  const resetForm = () => {
    setContractName("");
    setDescription("");
    setError("");
    setSelectedCountry("AE");
    setSelectedLanguage("en");
    setContractTypeName("");
    setSelectedJurisdictionCodes(null);
    setSelectedTemplate(null);
    setType("templates");
    setImportedFile(null);
    setIsDragging(false);
  };

  const handleCreate = async () => {
    if (type !== 'templates' && !contractName.trim()) {
      setError(t("name_required") || "Contract name is required");
      return;
    }
    if (type === 'templates' && !contractTypeName.trim()) {
      setError(t("contract_type_required") || "Please describe the contract you need");
      return;
    }
    if (type === 'templates' && !selectedCountry) {
      setError(t("select_country_first") || "Please select a country");
      return;
    }

    try {
      setCreating(true);
      setError("");

      let newContract;

      if (type === 'templates') {
        newContract = await backendClient.generateContract({
          creatorId: user.id,
          country: selectedCountry,
          language: selectedLanguage,
          contractTypeName: contractTypeName.trim(),
          jurisdictionCodes: selectedJurisdictionCodes,
        });
      } else if (type === 'import' && importedFile) {
        const formData = new FormData();
        formData.append('file', importedFile);
        formData.append('title', contractName.trim());
        formData.append('creator_id', user.id);
        if (description.trim()) formData.append('description', description.trim());
        if (selectedCountry) formData.append('country', selectedCountry);
        newContract = await backendClient.importContract(formData);
        await backendClient.shareContract(newContract.id, [{ user_id: user.id, role: 'creator' }], 'app');
      } else {
        newContract = await backendClient.createContract(
          contractName.trim(),
          type === 'my_templates' ? selectedTemplate?.id : null,
          user.id,
          description.trim() || null,
          type,
          selectedCountry || null
        );
      }

      console.log("Created contract response:", newContract);
      if (!newContract || !newContract.id) {
        throw new Error(t("invalid_contract_response") || "Invalid contract response from server");
      }

      onClose();
      if (onSuccess) onSuccess(newContract.id);
      dispatch(resetContract());
      navigate(`/contract/${newContract.id}`, { replace: true });

    } catch (err) {
      console.error("Failed to create contract:", err);
      setError(err.message || t("contract_creation_error") || "Failed to create contract. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedTemplate(null);
    // A jurisdiction code is country-scoped, so changing country must reset it.
    setSelectedJurisdictionCodes(null);
  };

  const getAcceptedFileTypes = () => supportedFormats.map(f => `.${f.value}`).join(',');

  const validateFile = (file) => {
    if (!file) return false;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.some(f => f.value === ext)) {
      setError(`File format not supported. Please use ${supportedFormats.map(f => f.label).join(', ')}`);
      return false;
    }
    return true;
  };

  const processFile = (file) => {
    if (validateFile(file)) {
      if (!contractName) setContractName(file.name.replace(/\.[^/.]+$/, ""));
      setImportedFile(file);
      setError("");
      return true;
    }
    return false;
  };

  const handleFileImport = (e) => { const file = e.target.files[0]; if (file) processFile(file); };
  const handleRemoveFile = () => { setImportedFile(null); if (inputRef.current) inputRef.current.value = ""; };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); if (!creating) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); if (!creating) setIsDragging(true); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (creating) return;
    if (e.dataTransfer.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      if (processFile(file)) setType('import');
    }
  };

  const menuOptions = [
    { id: 'templates',    label: t('generate')     || 'Generate' },
    { id: 'my_templates', label: t('my_templates') || 'My Templates', isNew: true },
    { id: 'import',       label: t('import')       || 'Import' },
    { id: 'empty',        label: t('empty')        || 'Empty' },
  ];

  const isDisabled =
    creating ||
    (type === 'templates'    && !contractTypeName.trim()) ||
    (type === 'templates'    && !selectedCountry) ||
    (type === 'my_templates' && !selectedTemplate) ||
    (type === 'import'       && !importedFile) ||
    (type !== 'templates'    && !contractName.trim());

  const chipSx = (selected) => ({
    backgroundColor: selected ? dk.chipBgActive : dk.chipBg,
    color: selected ? dk.chipTextActive : dk.chipText,
    transition: 'all 0.18s ease',
    '&:hover': { backgroundColor: selected ? dk.chipBgActive : dk.hover },
    height: 26,
    borderRadius: '6px',
    '& .MuiChip-label': {
      fontSize: '0.72rem',
      padding: '0 12px',
      fontWeight: selected ? 600 : 400,
    },
  });

  return (
    <Modal
      open={open}
      onClose={!creating ? handleClose : undefined}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0,0,0,0.82)',
            backdropFilter: 'blur(12px)',
          },
        },
      }}
    >
      <Fade in={open} timeout={220}>
        <Box
          data-tour="contract-create-dialog"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: mobile ? '100vw' : { xs: '96vw', md: '820px' },
            height: mobile ? '100dvh' : '84vh',
            outline: 'none',
            borderRadius: mobile ? 0 : '14px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: dk.surface,
            border: mobile ? 'none' : `1px solid ${dk.goldBdr}`,
            boxShadow: `0 40px 100px rgba(0,0,0,0.9), 0 0 0 1px ${dk.borderLight}`,
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 2.5 },
              py: 1.5,
              borderBottom: `1px solid ${dk.border}`,
              background: `linear-gradient(180deg, ${dk.elevated} 0%, ${dk.surface} 100%)`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: dk.goldSub,
                  border: `1px solid ${dk.goldBdr}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FilePlus2 size={14} color={dk.gold} />
              </Box>
              <Typography
                sx={{
                  color: dk.text1,
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
              >
                {t("new_contract") || "New Contract"}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              disabled={creating}
              size="small"
              sx={{
                color: dk.text3,
                width: 28,
                height: 28,
                '&:hover': { color: dk.text1, backgroundColor: dk.hover },
              }}
            >
              <X size={15} />
            </IconButton>
          </Box>

          {/* ── Tab Chips ── */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              px: 2.5,
              py: 1.25,
              borderBottom: `1px solid ${dk.border}`,
              backgroundColor: dk.bg,
              flexShrink: 0,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {menuOptions.map((option) => (
              <Box key={option.id} sx={{ position: 'relative', flexShrink: 0 }}>
                <Chip
                  label={option.label}
                  onClick={() => setType(option.id)}
                  clickable
                  sx={chipSx(type === option.id)}
                />
                {option.isNew && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: dk.gold,
                      border: `1.5px solid ${dk.bg}`,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* ── Content ── */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: mobile ? 2 : 2.5,
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: dk.scrollThumb,
                borderRadius: 4,
              },
            }}
          >
            {type === 'templates' && (
              <TemplateOption
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                onCountryChange={handleCountryChange}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                contractTypeName={contractTypeName}
                setContractTypeName={setContractTypeName}
                selectedJurisdictionCodes={selectedJurisdictionCodes}
                setSelectedJurisdictionCodes={setSelectedJurisdictionCodes}
                mobile={mobile}
              />
            )}
            {type === 'my_templates' && (
              <MyTemplatesOption
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                error={error}
                contractName={contractName}
                setContractName={setContractName}
                description={description}
                setDescription={setDescription}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                onCountryChange={handleCountryChange}
                onSaveAsTemplate={() => ""}
                onNavigateToTemplate={(templateId) => {
                  onClose();
                  navigate(`/contract/template/${templateId}`);
                }}
              />
            )}
            {type === 'import' && (
              <ImportOption
                importedFile={importedFile}
                setImportedFile={setImportedFile}
                contractName={contractName}
                setContractName={setContractName}
                description={description}
                setDescription={setDescription}
                error={error}
                supportedFormats={supportedFormats}
                handleDragEnter={handleDragEnter}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                creating={creating}
                inputRef={inputRef}
                getAcceptedFileTypes={getAcceptedFileTypes}
                handleFileImport={handleFileImport}
                handleRemoveFile={handleRemoveFile}
                isDragging={isDragging}
                mobile={mobile}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                onCountryChange={handleCountryChange}
              />
            )}
            {type === 'empty' && (
              <EmptyOption
                contractName={contractName}
                setContractName={setContractName}
                description={description}
                setDescription={setDescription}
                error={error}
                mobile={mobile}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                onCountryChange={handleCountryChange}
              />
            )}

            {error && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: 'rgba(248,113,113,0.08)',
                  borderRadius: '8px',
                  border: '1px solid rgba(248,113,113,0.2)',
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', color: dk.error }}>
                  {error}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Footer ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.5,
              borderTop: `1px solid ${dk.border}`,
              backgroundColor: dk.bg,
              flexShrink: 0,
            }}
          >
            <Box
              onClick={!creating ? handleClose : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: creating ? 'default' : 'pointer',
                py: 0.5,
                px: 1,
                borderRadius: '6px',
                transition: 'all 0.15s',
                opacity: creating ? 0.4 : 1,
                '&:hover': creating ? {} : { backgroundColor: dk.hover },
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', color: dk.text3, fontWeight: 500 }}>
                {t("cancel") || "Cancel"}
              </Typography>
            </Box>

            <Box
              data-tour="contract-generate-btn"
              onClick={!isDisabled ? handleCreate : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: isDisabled ? 'default' : 'pointer',
                py: 0.65,
                px: 2,
                borderRadius: '8px',
                backgroundColor: isDisabled ? dk.chipBg : dk.gold,
                color: isDisabled ? dk.text3 : '#000',
                transition: 'all 0.18s ease',
                '&:hover': !isDisabled ? {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 16px ${dk.goldBdr}`,
                } : {},
              }}
            >
              {creating && (
                <CircularProgress size={12} sx={{ color: isDisabled ? dk.text3 : '#000000' }} />
              )}
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                {creating
                  ? (type === 'templates'
                    ? (t("generating_contract") || "Generating…")
                    : (t("creating") || "Creating..."))
                  : (type === 'templates'
                    ? (t("generate") || "Generate")
                    : (t("create") || "Create"))
                }
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateContractDialog;
